/**
 * My Bids tab content: loading, bids grid, bids pagination
 */

import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar, ChevronLeft, ChevronRight, Briefcase } from 'lucide-react';
import { Loader } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { cn } from '../../../utils/cn';

const GIGS_PAGE_SIZE = 10;

export interface BidListCardData {
  id: string;
  title: string;
  location: string;
  budgetDisplay: string;
  coinsDeducted: number;
  status: string;
  appliedDate?: string;
  rejectionReason?: string;
  canResubmit?: boolean;
  eventType: string;
  date: string;
  durationMinutes: number;
  attendees: number;
  description: string;
}

export interface MyBidsTabContentProps {
  bids: BidListCardData[];
  loading: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  bidsCount: number;
  onApplyAgain: (bid: BidListCardData) => void;
  applyAgainLoadingId?: string | null;
}

export const MyBidsTabContent: React.FC<MyBidsTabContentProps> = ({
  bids,
  loading,
  page,
  setPage,
  bidsCount,
  onApplyAgain,
  applyAgainLoadingId,
}) => {
  if (loading && bids.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-2xl p-12 text-center flex items-center justify-center"
      >
        <Loader size="small" />
      </motion.div>
    );
  }

  if (!loading && bids.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-2xl border border-[#3A3A3C] bg-[#1C1C1E] p-12 text-center"
      >
        <Briefcase className="h-14 w-14 text-[#8E8E93] mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium text-white mb-1">No bids yet</p>
        <p className="text-sm text-[#8E8E93]">
          Bids you place on gigs will appear here.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {bids.map((bid, index) => {
          const status = bid.status?.trim() || 'Unknown';
          const statusLower = status.toLowerCase();
          const statusStyles =
            statusLower.includes('reject')
              ? 'bg-red-500/10 text-red-500 border-red-500/20'
              : statusLower.includes('accept')
                ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                : 'bg-amber-500/10 text-amber-500 border-amber-500/20';
          const isRejected = statusLower.includes('reject');
          const rejectionReason = typeof bid.rejectionReason === 'string' ? bid.rejectionReason.trim() : '';
          const shouldShowRejectionReason =
            isRejected &&
            rejectionReason.length > 0 &&
            rejectionReason.toLowerCase() !== 'null' &&
            rejectionReason.toLowerCase() !== 'undefined';
          const canResubmit = Boolean(bid.canResubmit);
          const accent =
            statusLower.includes('reject')
              ? 'bg-red-500/60'
              : statusLower.includes('accept')
                ? 'bg-emerald-500/60'
                : 'bg-amber-500/60';

          return (
            <motion.article
              key={bid.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: index * 0.03 }}
              className="group relative overflow-hidden bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] p-5 transition-all hover:border-[#F33363]/30"
            >
              <div className={cn('absolute left-0 top-0 h-full w-1', accent)} />
              <div className="relative z-10 pl-2">
                <div className="flex items-start justify-between gap-3">
                  <h4 className="text-white font-bold text-base leading-snug line-clamp-2 group-hover:text-[#F33363] transition-colors">
                    {bid.title}
                  </h4>

                </div>
                <span
                  className={cn(
                    'flex-shrink-0 px-2 py-0.5 mt-1 rounded-full text-[10px] font-bold uppercase tracking-wider border w-fit',
                    statusStyles
                  )}
                >
                  {status}
                </span>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-[#8E8E93]">
                    <MapPin className="h-3.5 w-3.5 text-[#8E8E93]" />
                    <span className="truncate">{bid.location}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 items-end">
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-widest">Budget</p>
                      <p className="text-sm font-extrabold text-white truncate">{bid.budgetDisplay}</p>
                    </div>
                    <div className="text-right flex flex-col items-end gap-1">
                      <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-widest">
                        {bid.coinsDeducted} StarCoins <br /> deducted
                      </p>
                    </div>
                  </div>
                  {bid.appliedDate ? (
                    <div className="flex items-center gap-2 text-xs text-[#8E8E93]">
                      <Calendar className="h-3.5 w-3.5 text-[#8E8E93]" />
                      <span>Applied: {bid.appliedDate}</span>
                    </div>
                  ) : null}
                  {shouldShowRejectionReason ? (
                    <div className="rounded-lg bg-red-500/5">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-red-400">
                        Rejection Reason :
                      </p>
                      <p className="mt-1 text-xs text-red-200 whitespace-pre-wrap break-words">
                        {rejectionReason}
                      </p>
                    </div>
                  ) : null}
                  {canResubmit ? (
                    <Button
                      variant="active"
                      type="button"
                      onClick={() => onApplyAgain(bid)}
                      disabled={Boolean(applyAgainLoadingId) || loading}
                      isLoading={applyAgainLoadingId === bid.id}
                      className="w-full mt-1 py-2.5 text-sm font-bold"
                    >
                      Apply Again
                    </Button>
                  ) : null}
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>

      {bidsCount > 0 && (() => {
        const totalPages = Math.ceil(bidsCount / GIGS_PAGE_SIZE);
        if (totalPages <= 1) return null;

        const WINDOW_SIZE = 5;
        let startPage = 1;
        let endPage = totalPages;

        if (totalPages > WINDOW_SIZE) {
          if (page <= 3) {
            startPage = 1;
            endPage = WINDOW_SIZE;
          } else if (page >= totalPages - 2) {
            endPage = totalPages;
            startPage = totalPages - (WINDOW_SIZE - 1);
          } else {
            startPage = page - 2;
            endPage = page + 2;
          }
        } else {
          startPage = 1;
          endPage = totalPages;
        }

        const pagesToRender: number[] = [];
        for (let p = startPage; p <= endPage; p += 1) pagesToRender.push(p);

        return (
          <div className="flex items-center justify-center gap-2 pt-2 pb-4">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1 || loading}
              className={cn(
                'flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors min-w-[4.5rem]',
                page <= 1 || loading
                  ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                  : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]'
              )}
              aria-label="Previous page"
            >
              {loading ? (
                <Loader size="small" />
              ) : (
                <>
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </>
              )}
            </button>
            <div className="flex items-center gap-1">
              {pagesToRender.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  disabled={loading}
                  className={cn(
                    'min-w-[2.25rem] h-9 px-2 rounded-lg text-sm font-bold border transition-colors flex items-center justify-center',
                    p === page
                      ? 'border-[#F33363] bg-[#F33363] text-white'
                      : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]',
                    loading && 'opacity-70 cursor-not-allowed'
                  )}
                  aria-label={`Page ${p}`}
                  aria-current={p === page ? 'page' : undefined}
                >
                  {p === page && loading ? <Loader size="small" /> : p}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages || loading}
              className={cn(
                'flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors min-w-[4.5rem]',
                page >= totalPages || loading
                  ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                  : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]'
              )}
              aria-label="Next page"
            >
              {loading ? (
                <Loader size="small" />
              ) : (
                <>
                  Next
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        );
      })()}
    </div>
  );
};
